import React, { lazy, Suspense } from 'react';
import {
	Redirect,
	Route,
	Switch,
	useLocation,
	Link as RouterLink,
} from 'react-router-dom';
import get from 'lodash/get';

import Errors from 'components/structural/Errors';
import { navMap } from 'config/routingConfig';
import CartRouter from 'components/routers/CartRouter';
import ChatRouter from 'components/routers/ChatRouter';
import ShopRouter from 'components/routers/ShopRouter';
import SupportRouter from 'components/routers/SupportRouter';
import GlobalComponents from 'components/structural/GlobalComponents';
import DialogNewPortalWelcome from 'components/standard/DialogNewPortalWelcome';

import { ACCOUNT_CREATE_PATH } from 'components/routers/AccountRouter';
import LeftHandLayout from 'containers/LeftHandLayout';
import RoleRestrictedRoute from 'utility/routing/RoleRestrictedRoute';
import { roles } from 'utility/constants/roles';
import useAuthenticated, {
	useKeroOnly,
	useBasketAdmin,
	useAccountLogin,
	useSuspended,
} from 'modules/oidc/useAuthenticated';
import OidcCallback from 'containers/pages/oidc/Callback';
import { useAuth } from 'react-oidc-context';
import SuspenseFallback from './SuspenseFallback';
import LoginRedirect from './LoginRedirect';

const Cart = lazy(() => import('containers/pages/shop/Cart'));
const Logout = lazy(() => import('containers/structural/Logout'));
const NotFound = lazy(() => import('pages/error/NotFound'));
const LiveChatLoading = lazy(
	() => import('containers/structural/LiveChatLoading'),
);
const LiveChatUnavailable = lazy(
	() => import('containers/structural/LiveChatUnavailable'),
);
const News = lazy(() => import('containers/pages/login/News'));
const InvoicePDFDisplay = lazy(
	() => import('containers/pages/billing/invoice/InvoicePDFDisplay'),
);
const VNCConsole = lazy(
	() => import('containers/pages/servers/beyond-hosting/VNCConsole'),
);
const AccountCreate = lazy(() => import('containers/pages/account/create'));
const AccessDenied = lazy(() => import('pages/error/AccessDenied'));
const SupportCase = lazy(() => import('containers/pages/support/ticket'));
const RestrictionNoAccountLogin = lazy(
	() => import('components/standard/Restrictions/RestrictionNoAccountLogin'),
);

const AlwaysRoutesRaw = () => [
	<Route key="shopRoute" path="/shop">
		<ShopRouter navData={get(navMap, 'shop')} />
	</Route>,
	<Route key="cartBasketUuid" exact path="/cart/:basketUuid?">
		<Cart />
	</Route>,
	// legacy support for "saved baskets"
	<Route key="cartSavedBasketUuid" exact path="/cart/saved/:basketUuid">
		<Cart />
	</Route>,
];

const AuthenticatedRoutes = () => (
	<>
		<Switch>
			<Route path="/logout">
				<Logout />
			</Route>
			<Route path="/account/create">
				<AccountCreate />
			</Route>
			{Object.values(navMap).map((rootNav) => (
				<Route
					exact={get(rootNav, 'isExact')}
					key={get(rootNav, 'path')}
					path={get(rootNav, 'path')}
					render={get(rootNav, 'router')}
				/>
			))}
			<Route path="/cart-login">
				<Redirect to="/cart" />
			</Route>
			<Route path="/cart">
				<CartRouter />
			</Route>
			<Route path="/chat">
				<ChatRouter />
			</Route>
			<Route path="/oidc/callback">
				<OidcCallback />
			</Route>
			<RoleRestrictedRoute
				// legacy route
				exact
				whitelistedRoles={[
					roles.PURCHASER,
					roles.TECHNICIAN,
					roles.SUSPENDED_OWNER,
				]}
				path="/support/mytickets/:ticket"
				component={({ match }) => (
					<Redirect to={`/support/mycases/${match.params.ticket}`} />
				)}
			/>
			<RoleRestrictedRoute
				exact
				whitelistedRoles={[
					roles.PURCHASER,
					roles.TECHNICIAN,
					roles.SUSPENDED_OWNER,
				]}
				path="/support/mycases/:ticket"
				component={SupportCase}
			/>
			<Route path="/support">
				<SupportRouter />
			</Route>
			<Route path="/access-denied">
				<AccessDenied />
			</Route>
			<Route>
				<NotFound />
			</Route>
		</Switch>
		<DialogNewPortalWelcome />
	</>
);

const UnAuthenticatedRoutes = () => (
	<Switch>
		{AlwaysRoutesRaw()}
		<Route>
			<LoginRedirect />
		</Route>
	</Switch>
);

const KeroOnlyRoutes = () => (
	<Switch>
		{AlwaysRoutesRaw()}
		<Route path={ACCOUNT_CREATE_PATH}>
			<AccountCreate />
		</Route>
		<Route path="/logout">
			<Logout />
		</Route>
		<Redirect to={ACCOUNT_CREATE_PATH} />
	</Switch>
);

const externalSite = () => (
	<Switch>
		<Route path="/external/chatLoading">
			<LiveChatLoading />
		</Route>
		<Route path="/external/chatUnavailable">
			<LiveChatUnavailable />
		</Route>
		<Route path="/external/news">
			<News />
		</Route>
		<Route
			path="/external/invoicepdf/:id"
			render={(props) => <InvoicePDFDisplay {...props} />}
		/>
		<Route
			path="/external/vnc-console/:uniqId"
			render={(props) => <VNCConsole {...props} />}
		/>
		<Route>
			<NotFound />
		</Route>
	</Switch>
);

const Routes = ({ hasAccountLogin, isLoggedIn, isKeroOnly }) => {
	const { isLoading } = useAuth();
	if (isLoading) return <SuspenseFallback />;

	if (isLoggedIn && !hasAccountLogin)
		return <RestrictionNoAccountLogin sx={{ flexGrow: 1 }} />;
	if (isLoggedIn && isKeroOnly) return <KeroOnlyRoutes />;
	if (isLoggedIn) return <AuthenticatedRoutes />;
	return <UnAuthenticatedRoutes />;
};

const PageStructure = () => {
	const { pathname } = useLocation();
	const isLoggedIn = useAuthenticated();
	const isKeroOnly = useKeroOnly();
	const isBasketAdmin = useBasketAdmin();
	const hasAccountLogin = useAccountLogin();
	const isSuspended = useSuspended();

	return (
		<>
			<LeftHandLayout
				appPath={pathname}
				linkComponent={RouterLink}
				linkProp="to"
				roles={{
					loggedIn: isLoggedIn,
					keroOnly: isKeroOnly,
					basketAdmin: isBasketAdmin,
					accountLogin: hasAccountLogin,
					suspended: isSuspended,
				}}
				minHeight="100vh"
				minWidth="100vw"
				maxWidth="100vw"
			>
				<Suspense fallback={<SuspenseFallback />}>
					<Routes
						hasAccountLogin={hasAccountLogin}
						isLoggedIn={isLoggedIn}
						isKeroOnly={isKeroOnly}
					/>
				</Suspense>
			</LeftHandLayout>
			<Errors />
			<GlobalComponents />
		</>
	);
};

const RouteHandler = () => {
	return (
		<Suspense fallback={<SuspenseFallback />}>
			<Switch>
				<Route path="/external" component={externalSite} />
				<Route>
					<PageStructure />
				</Route>
			</Switch>
		</Suspense>
	);
};

export default RouteHandler;
